import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from '../services/local-storage.service';
import { TokenRefreshService } from '../services/token-refresh-service';
import { LoginApiResponse, TokenResponse } from 'src/app/api/identity/models';

const EXCLUDE_URL_LIST: string[] = ['/login'];

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private toastShown = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private tokenRefreshService: TokenRefreshService,
    private localStorageService: LocalStorageService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // if (EXCLUDE_URL_LIST.includes(request.url)) {
    //   return next.handle(request);
    // }
    if (request.url.includes('/login')) {
      return next.handle(request);
    }

    const authToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const isRefreshTokenRequest = request.url.includes('/refresh-token');

    const authRequest = this.addToken(
      request,
      authToken,
      refreshToken,
      isRefreshTokenRequest
    );

    return next.handle(authRequest).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401 && !isRefreshTokenRequest) {
            if (!authToken) {
              this.showAuthErrorOnce(error.error?.message);
            }
            return this.handle401Error(request, next);
          } else if (error.status === 400) {
            if (error.error && error.error.message) {
              // this.toastr.error('Bad Request.');
            } else {
              this.toastr.error('Bad Request.');
            }
          } else {
            console.log('default case error');
          }
        }
        return throwError(error);
      })
    );
  }

  private addToken(
    request: HttpRequest<any>,
    authToken: any,
    refreshToken: string | null,
    isRefreshTokenRequest: boolean
  ): HttpRequest<any> {
    const headers: any = {
      Authorization: `Bearer ${
        isRefreshTokenRequest ? refreshToken : authToken
      }`,
    };

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenRefreshService.refreshToken().pipe(
        switchMap((newTokenResponse: LoginApiResponse) => {
          this.isRefreshing = false;
          this.toastShown = false;
          this.refreshTokenSubject.next(newTokenResponse);
          return next.handle(this.addNewToken(request, newTokenResponse));
        }),
        catchError(error => {
          this.isRefreshing = false;
          // Notify waiting requests that refresh failed
          this.refreshTokenSubject.error(error);
          // Reset subject for next possible session
          this.refreshTokenSubject = new BehaviorSubject<any>(null);

          if (
            error instanceof HttpErrorResponse &&
            (error.status === 401 || error.status === 403)
          ) {
            this.logoutForExpiredSession(
              error.error?.message ||
                'Your session has expired. Please sign in again.'
            );
          }

          return throwError(() => error);
        })
      );
    } else {
      return this.waitForTokenRefresh().pipe(
        switchMap(() => {
          const newTokenResponse =
            this.refreshTokenSubject.getValue() as LoginApiResponse;
          return next.handle(this.addNewToken(request, newTokenResponse));
        })
      );
    }
  }

  private addNewToken(
    request: HttpRequest<any>,
    newTokenResponse: LoginApiResponse
  ): HttpRequest<any> {
    const tokenResponseArray = newTokenResponse.data;

    if (tokenResponseArray && tokenResponseArray.length > 0) {
      const tokenResponse: TokenResponse | undefined = tokenResponseArray[0];

      if (tokenResponse && tokenResponse.accessToken) {
        localStorage.setItem('token', tokenResponse.accessToken);

        // Token rotation: persist the new refresh token returned by the server
        // so the next refresh uses the rotated token, not the old consumed one.
        if (tokenResponse.refreshToken) {
          localStorage.setItem('refreshToken', tokenResponse.refreshToken);
        }

        return this.addToken(
          request,
          tokenResponse.accessToken,
          localStorage.getItem('refreshToken'),
          false
        );
      }
    }

    console.error('No valid access token found in the refresh token response');
    this.logoutForExpiredSession(
      'No valid access token found in the refresh token response'
    );
    throw new Error(
      'No valid access token found in the refresh token response'
    );
  }

  private showAuthErrorOnce(message: string): void {
    if (this.toastShown) {
      return;
    }

    this.toastShown = true;
    this.toastr.error(message);
  }

  private logoutForExpiredSession(message: string): void {
    this.showAuthErrorOnce(message);
    this.localStorageService.clear();

    // Let the toast attach to the current view before redirecting away.
    setTimeout(() => {
      this.router.navigate(['/auth']);
    }, 100);
  }

  private waitForTokenRefresh(): Observable<LoginApiResponse> {
    return this.refreshTokenSubject.asObservable().pipe(
      filter(token => token !== null),
      take(1)
    );
  }
}
